import React, { useEffect, useState } from 'react';
import { connect } from "react-redux";
import moment from 'moment-timezone';
import * as Tools from "../../helpers/Tools";
import * as types from "../../constants/actionTypes";
import { logUI } from "../../api/andyliticsApi";
import { Redirect } from "react-router-dom";
import Spinner from '../common/spinner';
import InHomeSchedulingButtons from './InHomeSchedulingButtons';
import AppointmentOverview from './AppointmentOverview';
import SchedulingQuestions from './SchedulingQuestions';
import DatePicker from '../common/DatePicker'
import SectionLoadingError from '../common/SectionLoadingError';
import StickyProgressBar from '../common/StickyProgressBar';


const InHomeWhen = ({ history, eligibilityResponses, dateTimeError, getAvailableTimes, setApptTime, availableApptsPending, availableDateTimes, enabledDays, selectedDateTime, refreshInterval }) => {

    const [selectedDay, setSelectedDay] = useState(null);
    const [selectedTime, setSelectedTime] = useState(null);
    const [showWarning, setShowWarning] = useState(false);

    const timeZone = Tools.getCentralDateWithFormat(new Date(), "z")

    useEffect(() => {
        logUI(`MBHWeb.UI.InHomeSchedulingWhen.Displayed`, new Date(), Tools.getURLPath());
        getAvailableTimes();

    }, []);

    useEffect(() => { // Refresh data every two minutes
        const interval = setInterval(() => {
            logUI(`MBHWeb.UI.InHomeSchedulingWhen.TimesRefreshed`, new Date(), Tools.getURLPath());
            getAvailableTimes(true);
        }, refreshInterval);

        return () => clearInterval(interval);
    }, [refreshInterval])

    useEffect(() => {
        if (selectedDay && availableDateTimes) {
            let foundMatchingDay = availableDateTimes.find(d => {
                return d.slots.length && d.date === Tools.dateSimpleFormat(selectedDay);
            })
            if (!foundMatchingDay) {
                setSelectedDay(enabledDays[0])
                setSelectedTime(null);
                setApptTime(null)
            } else if (selectedTime && availableDateTimes) {
                let foundMatchingDateTime = availableDateTimes.find(dt => {
                    return dt.slots.find(slot => {
                        return slot === selectedTime;
                    })
                })
                if (!foundMatchingDateTime) {
                    setSelectedTime(null);
                    setApptTime(null)
                }
            }
        }
    }, [availableDateTimes])

    useEffect(() => {
        if (!selectedDay) {
            if (selectedDateTime && availableDateTimes && enabledDays) {
                let foundMatchingDateTime = availableDateTimes.find(dt => {
                    return dt.slots.find(slot => {
                        return slot === selectedDateTime;
                    })
                })
                if (!foundMatchingDateTime) {
                    setSelectedDay(enabledDays[0])
                    setSelectedTime(null);
                    setApptTime(null)
                }
                else {
                    let day = Tools.toJSDate(selectedDateTime)
                    day.setHours(0)
                    setSelectedDay(day);
                    setSelectedTime(selectedDateTime)
                }
            }
            else if (enabledDays) {
                setSelectedDay(enabledDays[0])
            }
        }
    }, [selectedDay, enabledDays, selectedDateTime, availableDateTimes])


    const handleTimeChange = React.useCallback(slot => setSelectedTime(slot), [])

    // eslint-disable-next-line react/display-name
    const TimeSlotItem = ({ slot, formattedTime, selectedTime }) => {
        return <label className="appt-btn-container" onClick={() => handleTimeChange(slot)}>
            <input
                type="radio"
                value={slot}
                defaultChecked={selectedTime === slot}
            />
            <div className="btn btn-primary">{formattedTime}</div>
        </label>;
    }

    const getSlots = (day) => availableDateTimes?.find(d => Tools
        .toJSDate(d.date, "YYYY-MM-DD")?.getTime() === day.getTime()
    )?.slots ?? []

    useEffect(() => {
        if (!selectedDay) return;
        const selectedDayString = Tools.dateSimpleFormat(new Date(selectedDay));

        if (selectedTime) {
            let timeObject = new Date(selectedTime)
            let timeString = Tools.dateSimpleFormat(timeObject);

            if (selectedDayString !== timeString) {
                setSelectedTime(null)
                setApptTime(null);
            }
        }

    }, [selectedDay])

    const timeButtons = React.useMemo(() => {
        if (!selectedDay) return <></>
        return getSlots(selectedDay)
            .map((slot, i) => {
                let date = Tools.toJSDate(slot);
                let formattedTime = moment(date).tz("America/Chicago").format("h:mm A")
                return <TimeSlotItem key={i} slot={slot} formattedTime={formattedTime} selectedTime={selectedTime} />
            })
            ?? <></>
    }, [selectedDay, availableDateTimes, handleTimeChange, selectedTime])

    const handleOnDayClick = React.useCallback((value, disabled) => {
        if (!disabled) {

            value.setHours(0);
            setSelectedDay(value);
        }
    }, [])

    const onClickPrevious = () => {
        logUI(`MBHWeb.UI.InHomeSchedulingWhen.Previous.Clicked`, new Date(), Tools.getURLPath());
        history.push("/inhomeschedule/s5")
    }

    const onClickNext = () => {
        if (selectedDay && selectedTime) {
            setApptTime(selectedTime);
            logUI(`MBHWeb.UI.InHomeSchedulingWhen.Next.Clicked`, new Date(), Tools.getURLPath());
            history.push('/inhomeschedule/s7');
        } else {
            setShowWarning(true)
        }
    }

    return (
        <>
            {!eligibilityResponses ? <Redirect to='/inhomeschedule/s5' /> :
                <>
                    <StickyProgressBar className="inhome" widthInPercent={5 / 6 * 100} />
                    <div className="container-fluid body-content inhome inhome-scheduling inhome-scheduling-container">
                        <div className="row">
                            <div className="col-12">
                                <div className="row flex">
                                    <div className="in-home-left-panel scheduling-wrapper">
                                        <div className="detail-wrapper when-detail-wrapper">
                                            <h1 className="page-heading">When would you like to schedule your appointment?</h1>
                                            <p className="subheading-info"></p>
                                            {availableApptsPending && <Spinner />}
                                            {dateTimeError && <SectionLoadingError message="Failed to load. Please try again" />}
                                            {enabledDays && <div className="date-picker-div">
                                                <DatePicker
                                                    enabledDays={enabledDays}
                                                    minDate={new Date()}
                                                    initialDate={enabledDays[0]}
                                                    selectedDays={selectedDay}
                                                    onClick={handleOnDayClick}
                                                    maxDate={enabledDays[enabledDays.length - 1]}
                                                    isInline />
                                            </div>}
                                            <div className="appt-buttons" role="radiogroup">
                                                {timeButtons}
                                            </div>
                                            <div className="subheading-info">
                                                All times are in {timeZone} time zone.
                                            </div>
                                            <InHomeSchedulingButtons leftOnClick={onClickPrevious} rightOnClick={onClickNext} showWarning={showWarning} warningText="Please select a time." />
                                        </div>
                                        <SchedulingQuestions />
                                    </div>
                                    <div className="in-home-right-panel">
                                        <AppointmentOverview datePhase={true} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            }
        </>
    );
}
const mapStateToProps = state => {
    let availableApptsResult = state.inHome.getAvailableApptsResult;
    let enabledDays = availableApptsResult?.data && availableApptsResult?.data.reduce((acc, day) => {
        //sometimes a day doesn't have any time slots left. if so, leave those out from the pickable days!
        if (day.slots?.length) acc.push(Tools.toJSDate(day.date, "YYYY-MM-DD"));
        return acc
    }, [])
    return {
        eligibilityResponses: state.inHome.eligibilityResponses,
        selectedUsers: state.inHome.selectedUsers,
        availableApptsPending: state.inHome.getAvailableApptsPending,
        dateTimeError: !state.inHome.getAvailableApptsPending && availableApptsResult?.success === false,
        availableDateTimes: availableApptsResult?.data,
        enabledDays: enabledDays,
        selectedDateTime: state.inHome.selectedDateTime,
        refreshInterval: state.session.serverData?.inHomeApptRefreshInterval || 120000
    }
}

const mapDispatchToProps = dispatch => {
    return {
        getAvailableTimes: (secret = false) => {
            dispatch({ type: types.GET_INHOME_APPTS, secret })
        },
        setApptTime: (selectedDateTime) => {
            dispatch({ type: types.SET_INHOME_DATETIME, selectedDateTime })
        }
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(InHomeWhen);